import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  AlertTriangle, TrendingUp, TrendingDown, Minus, Activity,
  Target, Users, DollarSign, BarChart3, Zap, Shield,
  AlertCircle, CheckCircle, Info, Send as SendIcon, Crosshair,
  Radio, Signal, Loader2, CalendarIcon, Lightbulb, ArrowRight,
  Megaphone, Handshake, Settings as SettingsIcon, Plus, ExternalLink, FileText, Stethoscope
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import DiagnosisReportModal from './DiagnosisReportModal';
import WarRoomDailyComparison from './WarRoomDailyComparison';

const API = process.env.REACT_APP_BACKEND_URL;

function pctChange(cur, prev) {
  if (!prev || prev === 0) return cur > 0 ? 100 : 0;
  return ((cur - prev) / prev * 100);
}

// ===== Reusable section components matching ChannelPerformanceContent =====
function SectionHeader({ icon: Icon, title, subtitle }) {
  return (
    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#E63946]/20 via-[#F97316]/15 to-transparent relative overflow-hidden rounded-t-xl">
      <div className="absolute inset-0 bg-gradient-to-r from-[#E63946]/10 to-transparent opacity-50" />
      <div className="relative flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#E63946]/30 to-[#F97316]/20 border border-[#E63946]/40 shadow-[0_0_15px_-3px_#E63946]">
          <Icon size={20} className="text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-white font-heading">{title}</h3>
          <p className="text-xs text-[hsl(var(--text-muted))]">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

function SectionCard({ children }) {
  return (
    <div className="relative bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
      {children}
    </div>
  );
}

// ===== Components =====
function TrendBadge({ value }) {
  if (value === 0) return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-[hsl(var(--surface))] text-[hsl(var(--text-muted))]"><Minus size={10} /> 0%</span>;
  if (value > 0) return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-[#10b981]/10 text-[#10b981]"><TrendingUp size={10} /> +{value.toFixed(1)}%</span>;
  return <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded bg-[#ef4444]/10 text-[#ef4444]"><TrendingDown size={10} /> {value.toFixed(1)}%</span>;
}

function MetricCard({ label, value, prevValue, format, target, icon: Icon }) {
  const change = pctChange(value, prevValue);
  const fmtVal = format === 'money' ? `RM ${value.toLocaleString('en-MY', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` :
    format === 'pct' ? `${value}%` : value.toLocaleString();
  const targetPct = target > 0 ? Math.min((value / target) * 100, 100) : 0;

  return (
    <div className="relative p-5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] hover:border-[hsl(var(--border-hover))] transition-all" data-testid={`wr-metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#7c3aed]/20 to-[#4f46e5]/10 border border-[#7c3aed]/30">
          <Icon size={16} className="text-[#a78bfa]" />
        </div>
        <TrendBadge value={change} />
      </div>
      <div className="text-2xl font-bold text-white font-mono tracking-tight">{fmtVal}</div>
      <div className="text-[11px] text-[hsl(var(--text-muted))] mt-1">{label}</div>
      {target > 0 && (
        <div className="mt-3">
          <div className="flex justify-between items-center mb-1">
            <span className="text-[9px] text-[hsl(var(--text-muted))]">Target Progress</span>
            <span className="text-[9px] font-semibold text-[hsl(var(--text-muted))]">{targetPct.toFixed(0)}%</span>
          </div>
          <div className="h-1.5 bg-[hsl(var(--surface))] rounded-full overflow-hidden">
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${targetPct}%`, background: targetPct >= 70 ? '#10b981' : targetPct >= 40 ? '#f59e0b' : '#ef4444' }} />
          </div>
        </div>
      )}
    </div>
  );
}

function HealthBadge({ score }) {
  const color = score >= 80 ? '#10b981' : score >= 50 ? '#f59e0b' : '#ef4444';
  const label = score >= 80 ? 'HEALTHY' : score >= 50 ? 'NEEDS ATTENTION' : 'CRITICAL';
  return (
    <div className="flex items-center gap-3" data-testid="wr-health-score">
      <div className="relative">
        <svg width="48" height="48" viewBox="0 0 48 48">
          <circle cx="24" cy="24" r="20" fill="none" stroke="hsl(var(--border))" strokeWidth="3" />
          <circle cx="24" cy="24" r="20" fill="none" stroke={color} strokeWidth="3"
            strokeDasharray={`${score * 1.26} 200`} strokeLinecap="round"
            transform="rotate(-90 24 24)" style={{ transition: 'stroke-dasharray 1s ease' }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold" style={{ color }}>{score}</span>
        </div>
      </div>
      <Badge className="text-[9px] font-bold uppercase tracking-wider" style={{ backgroundColor: `${color}20`, color, border: `1px solid ${color}40` }}>{label}</Badge>
    </div>
  );
}

function IssueCard({ issue }) {
  const icons = { critical: AlertTriangle, warning: AlertCircle, info: Info, good: CheckCircle };
  const colors = { critical: '#ef4444', warning: '#f59e0b', info: '#3b82f6', good: '#10b981' };
  const Icon = icons[issue.severity] || Info;
  const color = colors[issue.severity] || '#3b82f6';

  return (
    <div className="flex gap-3 p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] hover:border-[hsl(var(--border-hover))] transition-all" style={{ borderLeftWidth: 3, borderLeftColor: color }} data-testid={`wr-issue-${issue.severity}`}>
      <Icon size={16} style={{ color, flexShrink: 0, marginTop: 1 }} />
      <div>
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color }}>{issue.area}</span>
        <p className="text-[12.5px] text-white/80 mt-0.5 leading-relaxed">{issue.message}</p>
      </div>
    </div>
  );
}

function MiniChart({ data, dataKey, color, height = 36 }) {
  if (!data || data.length === 0) return <div className="text-[9px] text-[#6b7280] text-center py-2">No data</div>;
  const values = data.map(d => d[dataKey] || 0);
  const max = Math.max(...values, 1);
  const total = values.reduce((s, v) => s + v, 0);
  if (total === 0) return <div className="text-[9px] text-[#6b7280] text-center py-2">No data</div>;
  const w = 100 / values.length;
  return (
    <div>
      <svg width="100%" height={height} viewBox={`0 0 100 ${height}`} preserveAspectRatio="none">
        {values.map((v, i) => (
          <rect key={i} x={i * w + w * 0.12} y={height - (v / max * (height - 4))} width={w * 0.76}
            height={Math.max(v / max * (height - 4), 1)} rx="2" fill={color} opacity={i === values.length - 1 ? 1 : 0.45} />
        ))}
      </svg>
      <div className="text-[9px] font-mono text-[#9ca3af] mt-1 text-right">{total.toLocaleString()}</div>
    </div>
  );
}

function TelegramCard({ tg }) {
  const growth = tg.subscribers - tg.prev_subscribers;
  return (
    <div className="p-4 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] hover:border-[hsl(var(--border-hover))] transition-all">
      <div className="flex items-center gap-2 mb-3">
        <Badge className="text-[8px] font-bold uppercase bg-[#3b82f6]/15 text-[#3b82f6] border-[#3b82f6]/30">{tg.type}</Badge>
        <span className="text-[12px] font-semibold text-white">{tg.title}</span>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <div><div className="text-[15px] font-bold text-white font-mono">{tg.subscribers.toLocaleString()}</div><div className="text-[9px] text-[hsl(var(--text-muted))]">subscribers</div></div>
        <div><div className={`text-[15px] font-bold font-mono ${growth >= 0 ? 'text-[#10b981]' : 'text-[#ef4444]'}`}>{growth >= 0 ? '+' : ''}{growth}</div><div className="text-[9px] text-[hsl(var(--text-muted))]">growth</div></div>
        <div><div className="text-[15px] font-bold text-white font-mono">{tg.views_per_post.toLocaleString()}</div><div className="text-[9px] text-[hsl(var(--text-muted))]">views/post</div></div>
        <div><div className="text-[15px] font-bold text-white font-mono">{tg.notification_rate}%</div><div className="text-[9px] text-[hsl(var(--text-muted))]">notif rate</div></div>
      </div>
    </div>
  );
}

// ===== Main Component =====
export default function WarRoomContent({ ib }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingToLog, setAddingToLog] = useState(null);
  const [diagnosisOpen, setDiagnosisOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { if (ib?.id) fetchData(); }, [ib?.id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/war-room/${ib.id}`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        // If no content score, trigger calculation on-demand
        if (!d.content_score || !d.content_score.score) {
          try {
            const csRes = await fetch(`${API}/api/content-scores/${ib.id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (csRes.ok) {
              const cs = await csRes.json();
              if (cs && cs.score) d.content_score = cs;
            }
          } catch {}
        }
        setData(d);
      }
      else toast.error('Failed to load War Room data');
    } catch { toast.error('Error loading data'); }
    finally { setLoading(false); }
  };

  const addToLog = async (opt, dept) => {
    // Save optimization to backend channel-logs (so it appears in Log page after refresh)
    if (!ib?.id) { toast.error('No channel selected'); return; }
    const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
    const today = new Date().toISOString().slice(0, 10);
    const newEntry = {
      id: Date.now(),
      department: dept,
      date: today,
      author: user.name || 'War Room',
      issueType: opt.issue_type || '',
      status: 'pending',
      priority: opt.priority === 'high' ? 'high' : 'medium',
      impact: opt.priority === 'high' ? 'high' : 'medium',
      title: `[WAR ROOM] ${opt.title}`,
      description: opt.strategy,
      actionTaken: (opt.tactics || []).map((t, i) => `${i + 1}. ${t}`).join('\n'),
      metric: opt.metric || '',
      currentScore: '',
      expectedOutcome: opt.expected_impact || '',
      endResult: '',
      remarks: `Auto-generated from War Room — ${data?.ib_name || ''}`,
      reviewDate: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/channel-logs/${ib.id}`, newEntry, { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 });
      setAddingToLog(opt.title);
      setTimeout(() => setAddingToLog(null), 2000);
      toast.success(`Added to ${dept.charAt(0).toUpperCase() + dept.slice(1)} Log — view in Log tab`);
    } catch (err) {
      console.error('Add to log failed:', err);
      toast.error('Failed to add to log — try again');
    }
  };

  const addQuickActionToLog = async (title, dept, issueType, priority) => {
    if (!ib?.id) { toast.error('No channel selected'); return; }
    const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
    const today = new Date().toISOString().slice(0, 10);
    const newEntry = {
      id: Date.now(),
      department: dept,
      date: today,
      author: user.name || 'War Room',
      issueType: issueType,
      status: 'pending',
      priority: priority,
      impact: priority,
      title: `[WAR ROOM] ${title}`,
      description: `Quick action from War Room — ${data?.ib_name || ''}`,
      actionTaken: '', metric: '', currentScore: '', expectedOutcome: '', endResult: '', remarks: '',
      reviewDate: '',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/channel-logs/${ib.id}`, newEntry, { headers: { Authorization: `Bearer ${token}` }, timeout: 10000 });
      toast.success(`Added to ${dept.charAt(0).toUpperCase() + dept.slice(1)} Log`);
    } catch (err) {
      console.error('Quick log failed:', err);
      toast.error('Failed to add to log');
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20 gap-2">
      <Loader2 size={18} className="animate-spin text-[#E63946]" />
      <span className="text-sm text-[hsl(var(--text-muted))]">Loading War Room...</span>
    </div>
  );

  if (!data) return <div className="text-center py-20 text-[hsl(var(--text-muted))]">No data available</div>;

  const { mtd, prev_mtd, targets, telegram, operations, issues, health_score, daily_trend, comparison, summary, optimizations = [], content_score } = data;
  const deptIcons = { marketing: Megaphone, sales: Handshake, operation: SettingsIcon };

  return (
    <>
      {/* Sticky Header - matching Channel Performance style */}
      <header className="md:sticky md:top-0 z-20 bg-[#09090B]/95 backdrop-blur-md border-b border-[hsl(var(--border))]">
        <div className="px-4 sm:px-8 py-3 sm:py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h1 className="text-lg sm:text-2xl font-bold font-heading tracking-tight">Channel War Room</h1>
            <p className="text-xs sm:text-sm text-[hsl(var(--text-muted))]">{data.ib_name} — Live Performance Diagnostics</p>
          </div>
          <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
            <button
              onClick={() => setDiagnosisOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#E63946] to-[#c62828] hover:brightness-110 text-white text-[11px] font-bold shadow-lg shadow-[#E63946]/25 transition-all"
              data-testid="full-diagnosis-btn"
              title="AI-powered full channel diagnosis dalam Bahasa Melayu"
            >
              <Stethoscope size={13} />
              Full Diagnosis
            </button>
            <div className="flex items-center gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg bg-[hsl(var(--surface))] border border-[hsl(var(--border))]">
              <CalendarIcon size={12} className="text-[hsl(var(--text-muted))]" />
              <span className="text-[10px] sm:text-xs font-medium text-white">MTD: Day {data.day} of {data.month}</span>
            </div>
            <HealthBadge score={health_score} />
          </div>
        </div>
      </header>

      <DiagnosisReportModal
        open={diagnosisOpen}
        onOpenChange={setDiagnosisOpen}
        ibId={ib.id}
        ibName={data.ib_name}
        brandLogoUrl={ib.brand_logo_url}
      />

      <div className="px-4 sm:px-8 py-4 sm:py-6 space-y-4 sm:space-y-6" data-testid="war-room">
        {/* QUICK SUMMARY */}
        {summary && (
          <div className="p-4 rounded-xl border border-[#7c3aed]/30 bg-gradient-to-r from-[#7c3aed]/10 to-transparent">
            <div className="flex items-start gap-3">
              <Crosshair size={16} className="text-[#a78bfa] mt-0.5 flex-shrink-0" />
              <p className="text-[13px] text-white/90 leading-relaxed" data-testid="wr-summary">{summary}</p>
            </div>
          </div>
        )}

        {/* DETECTED ISSUES */}
        <SectionCard>
          <SectionHeader icon={AlertTriangle} title="Detected Issues" subtitle="Auto-diagnosed problems & alerts" />
          <div className="p-6 space-y-2">
            {issues.map((iss, i) => <IssueCard key={i} issue={iss} />)}
          </div>
        </SectionCard>

        {/* COMPARISON: This Month vs Last Month */}
        {comparison && (
          <SectionCard>
            <SectionHeader icon={BarChart3} title="Month Comparison" subtitle={`${data.month} vs ${data.prev_month} (MTD Day ${data.day})`} />
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-[hsl(var(--border))]">
                      <th className="text-left py-2 text-[10px] font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider">Metric</th>
                      <th className="text-right py-2 text-[10px] font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider">{data.prev_month}</th>
                      <th className="text-right py-2 text-[10px] font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider">{data.month}</th>
                      <th className="text-right py-2 text-[10px] font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider">Change</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { label: 'Ad Spend', cur: comparison.current.spend, prev: comparison.previous.spend, fmt: 'money' },
                      { label: 'Subscribers', cur: comparison.current.subs, prev: comparison.previous.subs },
                      { label: 'Leads', cur: comparison.current.leads_sales, prev: comparison.previous.leads_sales },
                      { label: 'Closed', cur: comparison.current.closed, prev: comparison.previous.closed },
                      { label: 'Commission', cur: comparison.current.commission, prev: comparison.previous.commission, fmt: 'money' },
                      { label: 'Deposit', cur: comparison.current.deposit, prev: comparison.previous.deposit, fmt: 'money' },
                      { label: 'Active Client', cur: comparison.current.active_client, prev: comparison.previous.active_client },
                      { label: 'Traded Lot', cur: comparison.current.traded_lot, prev: comparison.previous.traded_lot },
                      {
                        label: 'Avg Lot/Client',
                        cur: comparison.current.active_client > 0 ? +(comparison.current.traded_lot / comparison.current.active_client).toFixed(2) : 0,
                        prev: comparison.previous.active_client > 0 ? +(comparison.previous.traded_lot / comparison.previous.active_client).toFixed(2) : 0,
                        fmt: 'decimal'
                      },
                      {
                        label: 'Commission/Client',
                        cur: comparison.current.active_client > 0 ? +(comparison.current.commission / comparison.current.active_client).toFixed(2) : 0,
                        prev: comparison.previous.active_client > 0 ? +(comparison.previous.commission / comparison.previous.active_client).toFixed(2) : 0,
                        fmt: 'money'
                      },
                      {
                        label: 'Com/Lot',
                        cur: comparison.current.com_per_lot || 0,
                        prev: comparison.previous.com_per_lot || 0,
                        fmt: 'usd'
                      },
                    ].map(row => {
                      const change = row.prev > 0 ? ((row.cur - row.prev) / row.prev * 100) : (row.cur > 0 ? 100 : 0);
                      const fmtV = (v) => row.fmt === 'money' ? `RM ${v.toLocaleString('en-MY', {minimumFractionDigits:0, maximumFractionDigits:0})}`
                        : row.fmt === 'usd' ? `$${(v || 0).toFixed(2)}`
                        : row.fmt === 'decimal' ? (v || 0).toFixed(2)
                        : v.toLocaleString();
                      return (
                        <tr key={row.label} className="border-b border-[hsl(var(--border))]/50">
                          <td className="py-2.5 text-xs font-medium text-white">{row.label}</td>
                          <td className="py-2.5 text-xs text-right text-[hsl(var(--text-muted))] font-mono">{fmtV(row.prev)}</td>
                          <td className="py-2.5 text-xs text-right text-white font-mono font-semibold">{fmtV(row.cur)}</td>
                          <td className="py-2.5 text-right"><TrendBadge value={change} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </SectionCard>
        )}

        {/* DAILY MTD COMPARISON */}
        <WarRoomDailyComparison ibId={ib.id} />

        {/* KEY METRICS */}
        <SectionCard>
          <SectionHeader icon={BarChart3} title="Key Metrics (MTD)" subtitle="Month-to-date performance vs previous month" />
          <div className="p-6">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              <MetricCard label="Ad Spend" value={mtd.marketing.spend} prevValue={prev_mtd.marketing.spend} format="money" icon={DollarSign} />
              <MetricCard label="Subscribers" value={mtd.marketing.subs} prevValue={prev_mtd.marketing.subs} target={targets.subscribers || 0} icon={Users} />
              <MetricCard label="CPS" value={mtd.cps} prevValue={prev_mtd.marketing.spend > 0 && prev_mtd.marketing.subs > 0 ? +(prev_mtd.marketing.spend / prev_mtd.marketing.subs).toFixed(2) : 0} format="money" icon={Target} />
              <MetricCard label="Leads" value={mtd.sales.leads} prevValue={prev_mtd.sales.leads} icon={SendIcon} />
              <MetricCard label="Closed" value={mtd.sales.closed} prevValue={prev_mtd.sales.closed} target={targets.closed || 0} icon={CheckCircle} />
              <MetricCard label="Closing Rate" value={mtd.closing_rate} prevValue={prev_mtd.sales.leads > 0 ? +(prev_mtd.sales.closed / prev_mtd.sales.leads * 100).toFixed(1) : 0} format="pct" icon={Target} />
              <MetricCard label="Commission" value={mtd.sales.commission} prevValue={prev_mtd.sales.commission} format="money" target={targets.commission || 0} icon={DollarSign} />
            </div>
          </div>
        </SectionCard>

        {/* OPTIMIZATIONS — The most important section */}
        <SectionCard>
          <SectionHeader icon={Lightbulb} title="Optimization Strategies" subtitle="Data-driven solutions to improve performance" />
          <div className="p-6">
            {/* Quick Actions row */}
            <div className="flex flex-wrap gap-2 mb-5 pb-5 border-b border-[hsl(var(--border))]">
              <span className="text-[10px] font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider mr-2 self-center">Quick:</span>
              {[
                { label: 'Flag to Team', dept: 'operation', type: 'process', prio: 'high', color: '#ef4444' },
                { label: 'Request Budget', dept: 'marketing', type: 'campaign', prio: 'high', color: '#f59e0b' },
                { label: 'Review Marketing', dept: 'marketing', type: 'campaign', prio: 'medium', color: '#8b5cf6' },
                { label: 'Fix Tracking', dept: 'operation', type: 'system', prio: 'high', color: '#3b82f6' },
              ].map(qa => (
                <button key={qa.label}
                  onClick={() => addQuickActionToLog(qa.label + ' — ' + data.ib_name, qa.dept, qa.type, qa.prio)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold border transition-all hover:scale-[1.02]"
                  style={{ borderColor: `${qa.color}40`, color: qa.color, background: `${qa.color}10` }}
                  data-testid={`quick-action-${qa.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <Plus size={10} /> {qa.label} → Log
                </button>
              ))}
              <button onClick={() => navigate(`/ib/${ib.id}/details`)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-semibold border border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:text-white transition-all ml-auto" data-testid="view-log-btn">
                <ExternalLink size={10} /> View Log
              </button>
            </div>

            {/* Optimization cards by department */}
            {['marketing', 'sales', 'operation'].map(dept => {
              const deptOpts = optimizations.filter(o => o.department === dept);
              if (deptOpts.length === 0) return null;
              const DeptIcon = deptIcons[dept];
              const deptLabel = dept.charAt(0).toUpperCase() + dept.slice(1);
              const deptColors = { marketing: '#E63946', sales: '#10b981', operation: '#3b82f6' };

              return (
                <div key={dept} className="mb-5">
                  <div className="flex items-center gap-2 mb-3">
                    <DeptIcon size={14} style={{ color: deptColors[dept] }} />
                    <span className="text-xs font-bold uppercase tracking-wider" style={{ color: deptColors[dept] }}>{deptLabel}</span>
                  </div>
                  <div className="space-y-3">
                    {deptOpts.map((opt, idx) => (
                      <div key={idx} className="p-5 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))] hover:border-[hsl(var(--border-hover))] transition-all" data-testid={`optimization-${dept}-${idx}`}>
                        <div className="flex items-start justify-between gap-3 mb-3">
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="text-[14px] font-bold text-white">{opt.title}</h4>
                              <Badge className="text-[8px]" style={{ backgroundColor: `${opt.priority === 'high' ? '#ef4444' : '#f59e0b'}20`, color: opt.priority === 'high' ? '#ef4444' : '#f59e0b', border: `1px solid ${opt.priority === 'high' ? '#ef4444' : '#f59e0b'}40` }}>
                                {opt.priority === 'high' ? 'HIGH PRIORITY' : 'MEDIUM'}
                              </Badge>
                            </div>
                            <p className="text-[12px] text-white/70 leading-relaxed">{opt.strategy}</p>
                          </div>
                          <button
                            onClick={() => addToLog(opt, dept)}
                            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[10px] font-semibold whitespace-nowrap transition-all ${addingToLog === opt.title ? 'bg-[#10b981] text-white' : 'bg-[hsl(var(--surface))] border border-[hsl(var(--border))] text-[hsl(var(--text-muted))] hover:text-white hover:border-[#7c3aed]/50'}`}
                            data-testid={`add-to-log-${dept}-${idx}`}
                          >
                            {addingToLog === opt.title ? <><CheckCircle size={11} /> Added!</> : <><Plus size={11} /> Add to Log</>}
                          </button>
                        </div>
                        <div className="space-y-1.5 mb-3">
                          {opt.tactics.map((tactic, ti) => (
                            <div key={ti} className="flex gap-2 text-[11.5px] text-white/60 leading-relaxed">
                              <span className="text-[#7c3aed] font-bold shrink-0">{ti + 1}.</span>
                              <span>{tactic}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex items-center gap-4 pt-3 border-t border-[hsl(var(--border))]/50">
                          <span className="text-[10px] text-[#10b981] font-semibold">Expected: {opt.expected_impact}</span>
                          <span className="text-[10px] text-[hsl(var(--text-muted))]">Metric: {opt.metric}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </SectionCard>

        {/* 14-DAY TRENDS + TELEGRAM */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <SectionCard>
            <SectionHeader icon={Activity} title="14-Day Trends" subtitle="Daily performance snapshots" />
            <div className="p-4 sm:p-6 grid grid-cols-2 gap-2 sm:gap-3">
              {[
                { label: 'Ad Spend', key: 'spend', color: '#8b5cf6' },
                { label: 'Subscribers', key: 'subs', color: '#10b981' },
                { label: 'Sales Leads', key: 'leads_sales', color: '#3b82f6' },
                { label: 'Commission', key: 'commission', color: '#f59e0b' },
              ].map(c => (
                <div key={c.key} className="p-2.5 sm:p-3 rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-elevated))]">
                  <div className="text-[8px] sm:text-[9px] font-semibold text-[hsl(var(--text-muted))] uppercase tracking-wider mb-1.5">{c.label}</div>
                  <MiniChart data={daily_trend} dataKey={c.key} color={c.color} />
                </div>
              ))}
            </div>
          </SectionCard>

          <div className="space-y-6">
            <SectionCard>
              <SectionHeader icon={Radio} title="Telegram Channels" subtitle="Subscriber growth & engagement" />
              <div className="p-6 space-y-3">
                {telegram.length > 0 ? telegram.map((tg, i) => <TelegramCard key={i} tg={tg} />) : (
                  <p className="text-xs text-[hsl(var(--text-muted))]">No Telegram data available</p>
                )}
              </div>
            </SectionCard>

            {operations.total_trades > 0 && (
              <SectionCard>
                <SectionHeader icon={Signal} title="Signal Performance" subtitle="Trade win rate & statistics" />
                <div className="p-6">
                  <div className="flex gap-6 items-center">
                    {[
                      { label: 'Total Trades', val: operations.total_trades, color: 'white' },
                      { label: 'Wins', val: operations.wins, color: '#10b981' },
                      { label: 'Losses', val: operations.losses, color: '#ef4444' },
                      { label: 'Win Rate', val: `${operations.win_rate}%`, color: operations.win_rate >= 60 ? '#10b981' : '#f59e0b' },
                    ].map(s => (
                      <div key={s.label} className="text-center">
                        <div className="text-xl font-bold font-mono" style={{ color: s.color }}>{s.val}</div>
                        <div className="text-[9px] text-[hsl(var(--text-muted))] uppercase tracking-wider">{s.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </SectionCard>
            )}

            {/* Content Score Card */}
            {content_score && content_score.score > 0 && (
              <SectionCard>
                <SectionHeader icon={FileText} title="Content Quality Score" subtitle={`Based on ${content_score.post_count} recent posts`} />
                <div className="p-6">
                  <div className="flex items-center gap-6 mb-4">
                    <div className="w-20 h-20 rounded-2xl flex items-center justify-center border-2" style={{
                      borderColor: content_score.score >= 7 ? '#10b981' : content_score.score >= 4 ? '#f59e0b' : '#ef4444',
                      backgroundColor: content_score.score >= 7 ? '#10b98110' : content_score.score >= 4 ? '#f59e0b10' : '#ef444410',
                    }}>
                      <span className="text-3xl font-bold font-mono" style={{
                        color: content_score.score >= 7 ? '#10b981' : content_score.score >= 4 ? '#f59e0b' : '#ef4444'
                      }}>{content_score.score}</span>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-white mb-1">
                        {content_score.score >= 8 ? 'Excellent' : content_score.score >= 6 ? 'Good' : content_score.score >= 4 ? 'Average' : 'Needs Work'}
                      </div>
                      <div className="text-[10px] text-[hsl(var(--text-muted))]">
                        {content_score.post_count} posts analyzed | Avg {content_score.avg_views} views | {content_score.avg_reactions} reactions
                      </div>
                    </div>
                  </div>
                  {content_score.breakdown && (
                    <div className="space-y-2">
                      {Object.entries(content_score.breakdown).map(([key, val]) => (
                        <div key={key} className="flex items-center gap-3">
                          <span className="text-[9px] font-bold uppercase text-[hsl(var(--text-muted))] w-20">{key}</span>
                          <div className="flex-1 h-2 rounded-full bg-[hsl(var(--surface))] overflow-hidden">
                            <div className="h-full rounded-full transition-all" style={{
                              width: `${(val.score / 2) * 100}%`,
                              backgroundColor: val.score >= 1.5 ? '#10b981' : val.score >= 1 ? '#f59e0b' : '#ef4444'
                            }} />
                          </div>
                          <span className="text-[9px] font-mono text-white w-8 text-right">{val.score}/2</span>
                          <span className="text-[9px] text-[hsl(var(--text-muted))] flex-1 truncate">{val.detail}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {content_score.scored_at && (
                    <div className="mt-3 text-[9px] text-[hsl(var(--text-muted))]">
                      Last scored: {new Date(content_score.scored_at).toLocaleDateString('en-MY', { dateStyle: 'medium' })}
                    </div>
                  )}
                </div>
              </SectionCard>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
